const loadedBrandFonts = new Set<string>()
const loadedFontFaces = new Set<string>()

export interface BrandFontFace {
  family: string
  url: string
  weight?: string
  style?: string
}

/** Load a site's OWN @font-face files (extracted from its CSS) so slides render the REAL brand typeface,
 *  not a Google-Fonts lookalike. Files stream through the same-origin image proxy (font hosts rarely send
 *  CORS headers). Deduped per family+weight+style across the session. */
export function ensureFontFaces(faces?: BrandFontFace[] | null) {
  if (!faces?.length || typeof document === 'undefined') return
  for (const face of faces) {
    if (!face?.family || !face.url) continue
    const key = `${face.family.trim().toLowerCase()}|${face.weight ?? ''}|${face.style ?? ''}`
    if (loadedFontFaces.has(key)) continue
    loadedFontFaces.add(key)
    const src = `/deckgen-image-proxy-api?url=${encodeURIComponent(face.url)}`
    const css = [
      `@font-face { font-family: '${face.family.replace(/'/g, '')}';`,
      `src: url('${src}');`,
      face.weight ? `font-weight: ${face.weight};` : '',
      face.style ? `font-style: ${face.style};` : '',
      `font-display: swap; }`,
    ].join(' ')
    const style = document.createElement('style')
    style.setAttribute('data-brand-font-face', key)
    style.textContent = css
    document.head.appendChild(style)
  }
}

/** Best-effort: load a brand font so canvas previews AND generated slides render in the REAL typeface.
 *  We try Google Fonts and Fontshare (covers e.g. Poppins via Google, General Sans via Fontshare);
 *  whichever URL 404s just fails to apply, harmlessly. Deduped per family across the session. */
export function ensureBrandFont(family?: string) {
  if (!family || typeof document === 'undefined') return
  // Only a plausible family NAME makes a catalog URL. Callers feed this values read off real sites'
  // CSS, and a modern site's font-family is full of things that are not names — `var(--a-font-body)`
  // tokens, entity fragments like `&quot`, whole stacks — which turned into garbage catalog requests
  // on every boot (measured 2026-08-18: 5 × ERR_BLOCKED_BY_ORB per load of the default canvas). Take
  // the stack's lead family, and refuse anything still carrying CSS or markup syntax.
  const lead = leadFamily(family)
  if (!lead || lead.startsWith('--') || /[&(){};:<>@\\/="]/.test(lead)) return
  const key = lead.toLowerCase()
  if (!key || loadedBrandFonts.has(key)) return
  loadedBrandFonts.add(key)
  const google = lead.replace(/\s+/g, '+')
  const fontshare = lead.toLowerCase().replace(/\s+/g, '-')
  const hrefs = [
    `https://fonts.googleapis.com/css2?family=${google}:wght@400;500;600;700&display=swap`,
    `https://api.fontshare.com/v2/css?f[]=${fontshare}@400,500,600,700&display=swap`,
  ]
  for (const href of hrefs) {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = href
    link.setAttribute('data-brand-font', key)
    document.head.appendChild(link)
  }
}

// --- brand kit faces -------------------------------------------------------------------------------
//
// THE BUG THIS HALF EXISTS FOR (granola.ai, measured 2026-08-18 on Guri's own kit): a kit imported from a
// website stores every real `@font-face` file the site ships — six absolute woff2 URLs, correct — and then
// loads none of them. `ensureFontFaces` above was wired only to WEB CARDS, and every brand-kit surface went
// through the catalog loader instead, which knows Google and Fontshare and nothing else. Quadrant and
// Melange are on neither, so it was a silent no-op: canvas `measureText` for both families came back byte
// identical to a family invented on the spot. The name was right and the pixels were a system fallback.
//
// TWO things were broken, and fixing either alone still renders wrong:
//   1. the files were never fetched (above), and
//   2. the name the kit stores is not the name any file answers to. granola DECLARES `Quadrant Notepad`
//      and ships `@font-face { font-family: Quadrant }`. `styleImport.reconcileWithShipped` repairs that
//      on the Style record, but `profile.headingFont` — what the kit page and the theme recipe actually
//      read — keeps the declared name.
//
// So we alias rather than rewrite. A stored kit is left exactly as it is and the DECLARED name is made
// loadable, by emitting a second `@font-face` under that name pointing at the shipped file. That fixes
// kits already in the database with no migration, and it avoids the failure the style session measured
// when they tried repairing stored names directly: stripping markers off `Waldenburg FH` produced a THIRD
// name that matched nothing.

/** The first family in a stack, unquoted: `'Quadrant Notepad', sans-serif` → `Quadrant Notepad`. The kit
 *  page stores a full CSS stack when a font is picked from the catalog, and a bare family when it came
 *  from an import, so both shapes arrive here. */
export function leadFamily(raw?: string | null): string | undefined {
  const lead = raw?.split(',')[0]?.replace(/['"]/g, '').trim()
  return lead ? lead : undefined
}

/** Metric-adjustment faces, which ship no glyphs of their own. Mirrors `styleImport.isFallbackFamily`;
 *  duplicated deliberately, the same way `styleImport.shippedWeights` duplicates `moodboardStyle`, because
 *  this is a leaf util and importing the extractor here would pull the whole style layer into every render
 *  surface. Both read the same wire shape, so the rule is the same. */
function isMetricFallback(name: string): boolean {
  return /(^|[\s\-_])fallback([\s\-_]|$)/i.test(name) || /^adjusted[\s\-_]/i.test(name)
}

/**
 * The shipped family a declared name should resolve to, or undefined when it already resolves.
 *
 * Same conservative rule as `styleImport.reconcileWithShipped`, and deliberately no looser: an exact match
 * needs no alias, and only a provable relationship — one name is a prefix of the other, which is what a
 * marketing name like "Quadrant Notepad" over a file called "Quadrant" looks like — earns one. Longest
 * match wins, so `Quadrant Notepad Display` beats `Quadrant` for a declaration that named neither.
 */
function aliasTarget(declared: string, faces: readonly BrandFontFace[]): string | undefined {
  const shipped = faces.map((f) => f.family?.trim()).filter((f): f is string => !!f && !isMetricFallback(f))
  if (shipped.length === 0) return undefined
  const lower = declared.toLowerCase()
  if (shipped.some((f) => f.toLowerCase() === lower)) return undefined
  const related = shipped
    .filter((f) => {
      const s = f.toLowerCase()
      return s.startsWith(lower) || lower.startsWith(s)
    })
    .sort((a, b) => b.length - a.length)
  return related[0]
}

const quoteSafe = (s: string) => s.replace(/['\\]/g, '')

function faceRule(family: string, face: BrandFontFace, url: string): string {
  return [
    `@font-face{font-family:'${quoteSafe(family)}';`,
    `src:url('${url}') format('woff2');`,
    face.weight ? `font-weight:${quoteSafe(face.weight)};` : '',
    face.style ? `font-style:${quoteSafe(face.style)};` : '',
    `font-display:swap}`,
  ].join('')
}

/**
 * `@font-face` CSS for a kit's real files, including an aliased copy under each declared name.
 *
 * `via` decides where the bytes come from, and the two callers genuinely need different answers:
 *
 *   'proxy'  — same-origin through `/deckgen-image-proxy-api`. For OUR OWN pages (the kit page, the
 *              canvas). Font hosts often omit CORS headers, and that hop is already built and auth'd.
 *   'direct' — the brand's own URL. For the ARTIFACT, which runs in `sandbox="allow-scripts"` with no
 *              allow-same-origin, so it is on an OPAQUE origin: every request it makes is cross-origin,
 *              it carries no session cookie, and the proxy would 401 it. A direct URL is the only one
 *              that can work there, and it works whenever the brand's host sends CORS. MEASURED for
 *              granola.ai (Vercel/Next): `access-control-allow-origin: *`, including to `Origin: null`.
 *              A host that sends no CORS header falls back exactly as it does today, which is no worse
 *              than the status quo, and is why this is a string builder rather than a promise.
 */
export function brandFaceCss(
  faces: readonly BrandFontFace[] | null | undefined,
  declared: readonly (string | undefined)[] = [],
  via: 'proxy' | 'direct' = 'proxy',
): string {
  if (!faces?.length) return ''
  const usable = faces.filter((f) => f?.family && f.url)
  if (usable.length === 0) return ''
  const href = (u: string) => (via === 'direct' ? u : `/deckgen-image-proxy-api?url=${encodeURIComponent(u)}`)

  const rules = usable.map((f) => faceRule(f.family, f, href(f.url)))

  // One alias block per declared name that no file answers to. Every weight of the target family is
  // re-emitted under the declared name, so `font-weight:700` still picks the right file.
  const seen = new Set<string>()
  for (const raw of declared) {
    const name = leadFamily(raw)
    if (!name || seen.has(name.toLowerCase())) continue
    seen.add(name.toLowerCase())
    const target = aliasTarget(name, usable)
    if (!target) continue
    for (const f of usable) {
      if (f.family.trim().toLowerCase() !== target.toLowerCase()) continue
      rules.push(faceRule(name, f, href(f.url)))
    }
  }
  return rules.join('')
}

/** The kit whose faces every artifact document should carry. Module-level on purpose: `withDocBridge` is
 *  synchronous and is called from render surfaces that have no route to the kit (BirtvySlideCard and
 *  BirtvyCanvas are both held by other sessions tonight), so the alternative was threading a new argument
 *  through three components to say one global fact. Mirrors how `loadedBrandFonts` above already works. */
let activeFaces: { faces: BrandFontFace[]; declared: string[] } | null = null

const faceListeners = new Set<() => void>()

/**
 * Run `cb` whenever the active kit's faces CHANGE.
 *
 * An artifact's srcdoc is built once, at first render, and a sandboxed document cannot be restyled from
 * outside — so a kit that arrives over the network a moment later would be injected into a string nobody
 * reads again. Surfaces that render artifacts subscribe and reload the frame when this fires. Returns an
 * unsubscribe.
 */
export function onBrandFacesChange(cb: () => void): () => void {
  faceListeners.add(cb)
  return () => faceListeners.delete(cb)
}

/** Publish the active kit's faces. Safe to call repeatedly; the last call wins, which is what switching
 *  brand kits should do. Pass null to clear. Listeners fire only on a REAL change, so the common case —
 *  every generation re-fetching the same default kit — never reloads a frame. */
export function setActiveBrandFaces(
  faces?: readonly BrandFontFace[] | null,
  declared: readonly (string | undefined)[] = [],
) {
  const usable = (faces ?? []).filter((f) => f?.family && f.url)
  const next = usable.length
    ? { faces: usable.map((f) => ({ ...f })), declared: declared.filter((d): d is string => !!d) }
    : null
  const before = activeFaces ? activeBrandFaceCssForDoc() : ''
  activeFaces = next
  const after = activeFaces ? activeBrandFaceCssForDoc() : ''
  if (before === after) return
  for (const cb of faceListeners) {
    try {
      cb()
    } catch (e) {
      console.warn('[brandFonts] listener failed', e)
    }
  }
}

/** The active kit's faces as `@font-face` CSS for a SANDBOXED artifact document. Empty string when no kit
 *  is active, so callers can concatenate it unconditionally. */
export function activeBrandFaceCssForDoc(): string {
  if (!activeFaces) return ''
  return brandFaceCss(activeFaces.faces, activeFaces.declared, 'direct')
}

/** Load a kit's real faces into THIS document, aliases included, and publish them for artifacts. For our
 *  own pages: the kit page's samples and the canvas both render in the host document. */
export function ensureBrandKitFonts(
  faces?: readonly BrandFontFace[] | null,
  declared: readonly (string | undefined)[] = [],
) {
  setActiveBrandFaces(faces, declared)
  if (typeof document === 'undefined' || !faces?.length) return
  const css = brandFaceCss(faces, declared, 'proxy')
  if (!css) return
  const key = `kit:${css.length}:${faces.map((f) => f.url).join('|')}`
  if (loadedFontFaces.has(key)) return
  loadedFontFaces.add(key)
  const style = document.createElement('style')
  style.setAttribute('data-brand-font-face', 'kit')
  style.textContent = css
  document.head.appendChild(style)
}
